import { useEffect, useMemo, useState } from 'react';
import { SearchableSelect } from './SearchableSelect';

/**
 * Reużywalna paginacja client-side dla wszystkich zestawień w portalu.
 *
 * Założenia:
 * - Filtrowanie i sortowanie po stronie klienta robimy PRZED paginacją (tj. najpierw
 *   `filtered = items.filter(...)`, potem przekazujemy `filtered` do {@link usePaginated}).
 * - Wybrany rozmiar strony (jeśli podano `storageKey`) zapisuje się do localStorage,
 *   żeby kolejne wizyty pamiętały preferencję usera.
 * - Domyślny rozmiar strony to 25, opcje: 10 / 25 / 50 / 100 / „wszystko".
 *   „Wszystko" reprezentowane przez `pageSize === 0` (bez slice'owania).
 */

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface UsePaginatedOpts {
  defaultPageSize?: number;
  /** Klucz do localStorage — jeśli podany, preferowany pageSize zostaje zapamiętany. */
  storageKey?: string;
}

interface UsePaginatedResult<T> {
  pagedItems: T[];
  page: number;
  pageSize: number;
  setPage: (p: number) => void;
  setPageSize: (n: number) => void;
  total: number;
  totalPages: number;
}

/**
 * Hook do paginowania dowolnej tablicy. Automatycznie zwija page jeśli pageSize/items
 * zostały zmniejszone i aktualna strona „wystaje" poza zakres (typowy use case: po
 * filtrze tekstowym lista skraca się, więc strona 5 może już nie istnieć).
 */
export function usePaginated<T>(items: T[], opts?: UsePaginatedOpts): UsePaginatedResult<T> {
  const defaultPageSize = opts?.defaultPageSize ?? DEFAULT_PAGE_SIZE;
  const storageKey = opts?.storageKey;

  const [pageSize, setPageSizeState] = useState<number>(() => {
    if (storageKey && typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('pagination:' + storageKey);
      if (stored != null) {
        const n = Number(stored);
        if (!Number.isNaN(n) && n >= 0) return n;
      }
    }
    return defaultPageSize;
  });
  const [page, setPage] = useState(1);

  const total = items.length;
  const totalPages = pageSize === 0 ? 1 : Math.max(1, Math.ceil(total / pageSize));

  // Auto-clamp gdy strona wystaje poza zakres (np. po przefiltrowaniu listy).
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const setPageSize = (n: number) => {
    setPageSizeState(n);
    setPage(1);
    if (storageKey && typeof localStorage !== 'undefined') {
      try { localStorage.setItem('pagination:' + storageKey, String(n)); } catch { /* QuotaExceeded itp. */ }
    }
  };

  const pagedItems = useMemo(() => {
    if (pageSize === 0) return items;
    const start = (page - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, page, pageSize]);

  return { pagedItems, page, pageSize, setPage, setPageSize, total, totalPages };
}

interface PaginationProps {
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  /** Czy pokazywać opcję „wszystko" w selektorze (pageSize=0). Domyślnie tak. */
  showAllOption?: boolean;
  className?: string;
}

/**
 * Generuje listę numerów stron do pokazania w paginacji. Dla małych ilości pokazuje
 * wszystkie; dla większych — pierwsza, ostatnia, sąsiedztwo aktualnej (±1) i elipsy
 * w postaci stringa „…".
 */
function buildPageSequence(current: number, total: number): (number | '…')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const out: (number | '…')[] = [];
  const add = (v: number | '…') => out.push(v);
  add(1);
  if (current > 3) add('…');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    add(p);
  }
  if (current < total - 2) add('…');
  add(total);
  return out;
}

export function Pagination({
  total, page, pageSize, onPageChange, onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS, showAllOption = true,
  className = '',
}: PaginationProps) {
  if (total === 0) {
    // Brak wyników — i tak pokazujemy pasek z selektorem rozmiaru, żeby user widział
    // że paginacja jest aktywna i mógł zmienić preferencję, ale bez nawigacji stron.
    return (
      <div className={'flex items-center gap-3 py-2 px-1 text-[12px] text-ink-500 ' + className}>
        <span>Brak wyników</span>
        <PageSizeSelect
          pageSize={pageSize}
          onChange={onPageSizeChange}
          options={pageSizeOptions}
          showAllOption={showAllOption}
          className="ml-auto"
        />
      </div>
    );
  }

  const totalPages = pageSize === 0 ? 1 : Math.max(1, Math.ceil(total / pageSize));
  const startIdx = pageSize === 0 ? 1 : (page - 1) * pageSize + 1;
  const endIdx = pageSize === 0 ? total : Math.min(page * pageSize, total);

  const seq = buildPageSequence(page, totalPages);

  return (
    <div className={'flex flex-wrap items-center gap-2 py-2 px-1 text-[12px] ' + className}>
      <span className="text-ink-600">
        Pokazane <span className="font-mono">{startIdx}–{endIdx}</span> z <span className="font-mono">{total}</span>
      </span>

      <PageSizeSelect
        pageSize={pageSize}
        onChange={onPageSizeChange}
        options={pageSizeOptions}
        showAllOption={showAllOption}
        className="ml-3"
      />

      {totalPages > 1 && (
        <div className="ml-auto inline-flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => onPageChange(1)}
            disabled={page === 1}
            className="px-1.5 py-1 text-ink-500 hover:text-ink-900 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Pierwsza strona"
            aria-label="Pierwsza strona"
          >«</button>
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1}
            className="px-1.5 py-1 text-ink-500 hover:text-ink-900 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Poprzednia strona"
            aria-label="Poprzednia strona"
          >‹</button>
          {seq.map((p, idx) => (
            p === '…' ? (
              <span key={'e' + idx} className="px-1 text-ink-400">…</span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                aria-current={p === page ? 'page' : undefined}
                className={'min-w-[26px] h-[26px] px-1.5 rounded text-[11px] font-mono transition '
                  + (p === page
                      ? 'bg-water-500 text-white'
                      : 'text-ink-700 hover:bg-ink-100')}
              >{p}</button>
            )
          ))}
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages}
            className="px-1.5 py-1 text-ink-500 hover:text-ink-900 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Następna strona"
            aria-label="Następna strona"
          >›</button>
          <button
            type="button"
            onClick={() => onPageChange(totalPages)}
            disabled={page === totalPages}
            className="px-1.5 py-1 text-ink-500 hover:text-ink-900 disabled:opacity-30 disabled:cursor-not-allowed"
            title="Ostatnia strona"
            aria-label="Ostatnia strona"
          >»</button>
        </div>
      )}
    </div>
  );
}

interface PageSizeSelectProps {
  pageSize: number;
  onChange: (n: number) => void;
  options: number[];
  showAllOption: boolean;
  className?: string;
}

function PageSizeSelect({ pageSize, onChange, options, showAllOption, className = '' }: PageSizeSelectProps) {
  return (
    <label className={'inline-flex items-center gap-1.5 text-ink-600 ' + className}>
      <span>Wierszy na stronę:</span>
      <div className="w-[92px]">
        <SearchableSelect
          value={String(pageSize)}
          onChange={v => onChange(Number(v))}
          className="select-sm font-mono text-[11px] !py-0.5"
        >
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          {showAllOption && <option value={0}>wszystko</option>}
        </SearchableSelect>
      </div>
    </label>
  );
}
